import { Badge, BlockStack, Button, Card, InlineStack, ProgressBar, Text } from '@shopify/polaris';
import React from 'react';
import { FitmentImportHistoryItem } from '../../app/api/agent';

export const formatLabelMap: Record<string, string> = {
    csv_xlsx: 'CSV/XLSX',
    aces_xml: 'ACES XML',
    unknown: 'Unknown',
};

const formatStageLabel = (stage?: string) => {
    if (!stage) {
        return null;
    }

    switch (stage.trim().toLowerCase()) {
        case 'processing':
            return 'Processing';
        case 'parsing':
            return 'Parsing';
        case 'applying':
            return 'Applying';
        case 'tagging':
            return 'Tagging';
        case 'completed':
            return 'Completed';
        case 'failed':
            return 'Failed';
        default:
            return stage;
    }
};

export const getStatusBadge = (state: string) => {
    const normalizedState = state.trim().toLowerCase();

    if (normalizedState === 'completed' || normalizedState === 'complete') {
        return <Badge tone="success">Complete</Badge>;
    }

    if (normalizedState === 'failed' || normalizedState === 'error') {
        return <Badge tone="critical">Error</Badge>;
    }

    if (normalizedState === 'queued' || normalizedState === 'pending') {
        return <Badge tone="info">Queued</Badge>;
    }

    if (normalizedState === 'processing' || normalizedState === 'in_progress' || normalizedState === 'in progress') {
        return <Badge progress="partiallyComplete" tone="info">In progress</Badge>;
    }

    if (normalizedState === 'tagging in progress') {
        return <Badge progress="partiallyComplete" tone="info">Tagging In Progress</Badge>;
    }

    if (normalizedState === 'tagging complete') {
        return <Badge tone="success">Tagging Complete</Badge>;
    }

    if (normalizedState === 'tagging failed') {
        return <Badge tone="critical">Tagging Failed</Badge>;
    }

    if (normalizedState === 'cancelled' || normalizedState === 'canceled') {
        return <Badge tone="warning">Cancelled</Badge>;
    }

    return <Badge>{state}</Badge>;
};

export const getMappingRulesSummary = (item: FitmentImportHistoryItem) => {
    if (!item.restrictToVendor) {
        return 'Vendor mapping: Store-wide (no vendor restriction)';
    }

    const vendorLabel = item.selectedVendorName || 'Unknown vendor';
    const normalizeLabel = `SKU normalize: ${item.normalizeSku ? 'true' : 'false'}`;

    if (!item.useVendorSkuSuffixRule) {
        return `Vendor mapping: ${vendorLabel} · SKU suffix rule disabled · ${normalizeLabel}`;
    }

    const suffixRule = item.vendorSkuSuffixRule?.trim();
    const suffixRuleLabel = suffixRule ? suffixRule : '(empty)';
    const updateLabel = item.updateSavedVendorSkuSuffixRule ? 'saved as default' : 'import-only';

    return `Vendor mapping: ${vendorLabel} · Suffix "${suffixRuleLabel}" (${updateLabel}) · ${normalizeLabel}`;
};

const formatCount = (value?: number) => (typeof value === 'number' ? value.toLocaleString() : '0');

type StageProgress = {
    label: string;
    value: number;
};

const getStageProgress = (item: FitmentImportHistoryItem): StageProgress | null => {
    const stage = item.processingStage?.trim().toLowerCase();
    const declared = item.declaredRecordCount;
    const parsed = item.parsedRecordCount;
    const applied = item.appliedRecordCount;

    if (stage === 'parsing' && typeof declared === 'number' && declared > 0 && typeof parsed === 'number') {
        const value = Math.round((Math.min(parsed, declared) / declared) * 100);
        return {
            label: `Parsing rows ${formatCount(parsed)} / ${formatCount(declared)} (${value}%)`,
            value,
        };
    }

    if (stage === 'applying' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Applying rows ${formatCount(applied)} / ${formatCount(parsed)} (${value}%)`,
            value,
        };
    }

    if (stage === 'tagging') {
        if (typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0) {
            const processed = Math.max(0, Math.min(item.taggingProcessedCount ?? 0, item.taggingTotalCount));
            const value = Math.round((processed / item.taggingTotalCount) * 100);
            const phase = item.taggingPhase?.trim().toLowerCase();
            const actionLabel = phase === 'fetching' ? 'Fetching product tags' : 'Applying product tags';
            return {
                label: `${actionLabel} ${formatCount(processed)} / ${formatCount(item.taggingTotalCount)} (${value}%)`,
                value,
            };
        }

        const value = typeof item.progressPercent === 'number' ? Math.max(0, Math.min(100, item.progressPercent)) : 0;
        return {
            label: 'Tagging changed products',
            value,
        };
    }

    return null;
};

const getStageDiagnostics = (item: FitmentImportHistoryItem) => {
    const stage = item.processingStage?.trim().toLowerCase();

    if (!stage) {
        return null;
    }

    if (stage === 'parsing') {
        return 'Current step: parsing input file and normalizing source rows.';
    }

    if (stage === 'applying') {
        return 'Current step: writing parsed rows to fitment records and deduplicating.';
    }

    if (stage === 'tagging') {
        return 'Current step: refreshing product tags for changed fitments.';
    }

    return null;
};

type Props = {
    items: FitmentImportHistoryItem[];
    archivingIds: Record<number, boolean>;
    onArchive: (queueId: number) => void;
    onDownload: (item: FitmentImportHistoryItem) => void;
    downloadingIds: Record<number, boolean>;
};

const ImportHistoryCards = ({ items, archivingIds, onArchive, onDownload, downloadingIds }: Props) => {
    return (
        <BlockStack gap="200">
            {items.map((item) => {
                const progressValue = typeof item.progressPercent === 'number'
                    ? Math.max(0, Math.min(100, item.progressPercent))
                    : undefined;
                const stageLabel = formatStageLabel(item.processingStage);
                const isProcessingState = item.state.toLowerCase() === 'processing' || item.state.toLowerCase().includes('tagging');
                const showProgress = isProcessingState && (progressValue === undefined || progressValue < 100);
                const stageProgress = getStageProgress(item);
                const stageDiagnostics = getStageDiagnostics(item);

                return (
                    <Card key={item.queueId}>
                        <BlockStack gap="150">
                            <InlineStack align="space-between" blockAlign="center" gap="300">
                                <Text as="h3" variant="headingSm">
                                    {item.originalFileName || 'Unnamed file'}
                                </Text>

                                <InlineStack gap="200" blockAlign="center">
                                    {getStatusBadge(item.state)}
                                    <Button
                                        variant="plain"
                                        onClick={() => onDownload(item)}
                                        loading={!!downloadingIds[item.queueId]}
                                    >
                                        Download
                                    </Button>
                                    <Button
                                        tone="critical"
                                        variant="plain"
                                        onClick={() => onArchive(item.queueId)}
                                        loading={!!archivingIds[item.queueId]}
                                    >
                                        Archive
                                    </Button>
                                </InlineStack>
                            </InlineStack>

                            <InlineStack gap="300" blockAlign="center" wrap>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Queued: {new Date(item.addedOn).toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Format: {formatLabelMap[item.importFormat] ?? item.importFormat}
                                </Text>
                                {stageLabel && (
                                    <Text as="p" variant="bodySm" tone="subdued">
                                        Stage: {stageLabel}
                                    </Text>
                                )}
                            </InlineStack>

                            {showProgress && (
                                <BlockStack gap="100">
                                    <InlineStack align="space-between">
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            Overall progress
                                        </Text>
                                        {progressValue !== undefined && (
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                {progressValue}%
                                            </Text>
                                        )}
                                    </InlineStack>
                                    {progressValue !== undefined && <ProgressBar progress={progressValue} size="small" />}

                                    {stageProgress && (
                                        <BlockStack gap="050">
                                            <InlineStack align="space-between">
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {stageProgress.label}
                                                </Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {stageProgress.value}%
                                                </Text>
                                            </InlineStack>
                                            <ProgressBar progress={stageProgress.value} size="small" />
                                        </BlockStack>
                                    )}

                                    <Text as="p" variant="bodySm" tone="subdued">
                                        {item.processingMessage || 'Working on import...'}
                                    </Text>
                                </BlockStack>
                            )}

                            {stageDiagnostics && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    {stageDiagnostics}
                                </Text>
                            )}

                            <Text as="p" variant="bodySm">
                                {getMappingRulesSummary(item)}
                            </Text>

                            {(item.parsedRecordCount || item.appliedRecordCount || item.declaredRecordCount) && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Records Declared: {formatCount(item.declaredRecordCount)}, Parsed: {formatCount(item.parsedRecordCount)}, Applied: {formatCount(item.appliedRecordCount)}
                                </Text>
                            )}

                            {typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0 && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Tagging: Processed {formatCount(item.taggingProcessedCount)} / {formatCount(item.taggingTotalCount)}, Updated {formatCount(item.taggingUpdatedCount)}
                                </Text>
                            )}

                            {item.error && (
                                <Text as="p" variant="bodySm" tone="critical">
                                    Error: {item.error}
                                </Text>
                            )}
                        </BlockStack>
                    </Card>
                );
            })}
        </BlockStack>
    );
};

export default ImportHistoryCards;
